/**
 * A GRAVIDADE dentro do vermelho. Funções PURAS.
 *
 * A regra do farol não muda e não mora aqui: verde é ter folga, âmbar é folga curta e
 * vermelho é vencida, não caber ou estar sem material (`farol.util.ts`, no motor). O que
 * esta leitura faz é graduar o vermelho, porque numa carteira com 198 vermelhas de 210 a
 * cor sozinha parou de dizer por onde começar.
 *
 * NENHUMA conta nova: o atraso é o que o motor já calculou, em dias úteis. Ele chega de
 * dois jeitos conforme a tela (`diasAtraso`, positivo quando atrasa; ou `folgaDiasUteis`,
 * negativo quando estoura), e `atrasoDaFolga` traduz o segundo para o primeiro.
 *
 * Os dois cortes são parâmetro (Configurações › Prazo): escolha da uMode em 19/10 para
 * ordenar o vermelho, nunca para mudar a cor dele.
 */
import { IPrazoConfig } from '@/domain/configuracao/interfaces/configuracao';
import { FarolCor } from '@/domain/ops/interfaces/ops';

/**
 * Os quatro estados do vermelho. `semPrevisao` não é um grau: é a OP vermelha que não tem
 * data prevista nenhuma para comparar, e chamá-la de leve diria que ela está quase em dia.
 */
export type TGravidadeFarol = 'leve' | 'medio' | 'grave' | 'semPrevisao';

/** Os graus na ordem em que se lê um relatório: o pior primeiro. */
export const GRAVIDADES: ReadonlyArray<TGravidadeFarol> = ['grave', 'medio', 'leve'];

/** Até quantos dias úteis de atraso o vermelho é leve. Sugestão da uMode, a confirmar. */
export const GRAVIDADE_LEVE_ATE_PADRAO = 5;

/** Até quantos dias úteis de atraso o vermelho é médio; acima disso ele é grave. */
export const GRAVIDADE_MEDIO_ATE_PADRAO = 20;

export interface ICortesDaGravidade {
  leveAteDiasUteis: number;
  medioAteDiasUteis: number;
}

/**
 * Os cortes em vigor. Documento gravado antes dos campos (ou configuração ainda
 * carregando) cai nos padrões, que são os mesmos que o servidor semeia: a etiqueta nunca
 * pisca com um grau e corrige depois.
 */
export function cortesDaGravidade(prazo: IPrazoConfig | null | undefined): ICortesDaGravidade {
  return {
    leveAteDiasUteis: prazo?.gravidadeLeveAteDiasUteis ?? GRAVIDADE_LEVE_ATE_PADRAO,
    medioAteDiasUteis: prazo?.gravidadeMedioAteDiasUteis ?? GRAVIDADE_MEDIO_ATE_PADRAO,
  };
}

/**
 * O atraso em dias úteis a partir da FOLGA do motor, que é negativa quando a OP estoura o
 * prazo. Folga de zero para cima não é atraso nenhum. Sem folga calculada não há atraso
 * para ler: a OP não tem as duas datas que a conta compara.
 */
export function atrasoDaFolga(folgaDiasUteis: number | null | undefined): number | null {
  if (folgaDiasUteis === null || folgaDiasUteis === undefined || !Number.isFinite(folgaDiasUteis)) {
    return null;
  }
  return folgaDiasUteis < 0 ? -folgaDiasUteis : 0;
}

/**
 * A gravidade de uma OP VERMELHA; `null` em qualquer outra cor, porque graduar o verde ou
 * o âmbar seria inventar uma leitura que ninguém pediu.
 *
 * A OP vermelha por material pendente (ou por outro motivo sem atraso de data) entra pela
 * folga que tiver: com as duas datas, o atraso é zero e ela é leve, que é a verdade (ela
 * trava por outro motivo, não por tempo). Sem data prevista, ela é `semPrevisao`.
 */
export function gravidadeDoFarol(
  cor: FarolCor,
  atrasoDiasUteis: number | null | undefined,
  cortes: ICortesDaGravidade,
): TGravidadeFarol | null {
  if (cor !== 'VERMELHO') {
    return null;
  }
  if (atrasoDiasUteis === null || atrasoDiasUteis === undefined || !Number.isFinite(atrasoDiasUteis)) {
    return 'semPrevisao';
  }
  if (atrasoDiasUteis <= cortes.leveAteDiasUteis) {
    return 'leve';
  }
  return atrasoDiasUteis <= cortes.medioAteDiasUteis ? 'medio' : 'grave';
}

/**
 * O atraso de uma OP da Carteira, em dias úteis: o número que o MOTOR gravou
 * (`diasAtraso`, o mesmo da coluna e da Fila) e, na falta dele, o que a folga negativa
 * diz. Nenhum recálculo.
 */
export function atrasoDaOp(op: { diasAtraso?: number | null; folgaDiasUteis?: number | null }): number | null {
  return op.diasAtraso ?? atrasoDaFolga(op.folgaDiasUteis);
}

/**
 * O recorte do filtro "Gravidade" da Carteira. Os valores DENTRO do chip são
 * alternativas, como em todo filtro da barra. OP que não é vermelha nunca passa: o filtro
 * fala do vermelho.
 */
export function matchesGravidadeFiltro(
  cor: FarolCor,
  atrasoDiasUteis: number | null,
  graus: ReadonlyArray<TGravidadeFarol>,
  cortes: ICortesDaGravidade,
): boolean {
  if (graus.length === 0) {
    return true;
  }
  const gravidade = gravidadeDoFarol(cor, atrasoDiasUteis, cortes);
  return gravidade !== null && graus.includes(gravidade);
}

/**
 * Quanto uma OP corre na frente da outra: o vermelho grave abre a lista e o que não é
 * vermelho fica depois de todo vermelho. É por este número que a Fila abre ordenada e que
 * a coluna do farol da Carteira ordena.
 */
const PESO_DA_GRAVIDADE: Readonly<Record<TGravidadeFarol, number>> = {
  grave: 0,
  medio: 1,
  leve: 2,
  // Vermelha sem previsão fica no fim do vermelho: não dá para dizer que ela é pior que
  // uma que já se sabe atrasada em 30 dias úteis.
  semPrevisao: 3,
};

/** O peso das outras cores, depois de todo vermelho, na urgência de sempre. */
const PESO_DA_COR: Readonly<Record<FarolCor, number>> = {
  VERMELHO: 0,
  AMBAR: 10,
  VERDE: 11,
  CINZA: 12,
};

export function pesoDaUrgencia(cor: FarolCor, gravidade: TGravidadeFarol | null): number {
  return cor === 'VERMELHO' && gravidade !== null ? PESO_DA_GRAVIDADE[gravidade] : PESO_DA_COR[cor];
}

/** A classe de cada grau, escrita uma por uma: o Tailwind não vê nome montado em runtime. */
export interface IClassesDaGravidade {
  /** Preenchimento da bolinha, do ponto da etiqueta e do quadradinho da legenda. */
  fundo: string;
  /** Tinta que lê SOBRE o preenchimento. */
  texto: string;
}

export const CLASSES_DA_GRAVIDADE: Readonly<Record<TGravidadeFarol, IClassesDaGravidade>> = {
  leve: { fundo: 'bg-gravidade-leve', texto: 'text-gravidade-leve-fg' },
  medio: { fundo: 'bg-gravidade-medio', texto: 'text-gravidade-medio-fg' },
  grave: { fundo: 'bg-gravidade-grave', texto: 'text-gravidade-grave-fg' },
  // Sem previsão fica no vermelho de sempre: ela não é um dos três tons.
  semPrevisao: { fundo: 'bg-danger', texto: 'text-danger-fg' },
};
